import { useContext, useState } from "react";
import styles from "./FBT.module.css";
import { Button, Collapsible, Divider, FormLayout, InlineError, Label, RadioButton } from "@shopify/polaris";
import { BundleTypes } from "../constants";
import { asChoiceField } from "@shopify/react-form";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import { FBTBundleContext, FBTBundleContextType } from "./FBT";
import RenderSelectedProduct from "../../../components/RenderSelectedProduct/RenderSelectedProduct";
import BundleInfo from "../CommonFields/BundleInfo";
import Discount from "../CommonFields/Discount";
import useBundleMixpanelHook from "../../../hooks/useBundleMixpanelHook";
import AllowedProductPages from "../../../components/AllowedProductPages/AllowedProductPages";
import InclusionExclusionProducts from "../../../components/InclusionExclusionProducts/InclusionExclusionProducts";
import TextFields from "../CommonFields/TextFields";
import SwatchesSelectorWrapper from "../CommonFields/SwatchesSelectorWrapper";

const FBTContentForm = ({ showBundlerLevel = false }: { showBundlerLevel: boolean }) => {
    const [expandProductSetup, setExpandProductSetup] = useState(true);
    const context = useContext(FBTBundleContext);

    const {
        fields: {
            bundle_name,
            title,
            description,
            bundle_product_level,
            fbt_product_selection,
            ai_fbt_product_limit,
            show_ai_fbt_for,
            ai_fbt_allowed_products,
            products,
            offered_products,
            discount_type,
            discount_value,
            order_discounts,
            product_discounts,
            shipping_discounts,
            add_to_cart_cta_text,
            footer_text,
            footer_total_text,
            free_text,
            allowed_product_pages,
            render_product_pages_type,
            show_swatches,
            swatchOptions,
            swatchesProductsMap,
            swatchShape,
            swatchSize,
            bundles_design: { default_cta },
            offer_name,
            discount_label_template,
            manual_product_scope,
        },
        handleNext = () => {},
        dispatchBundleConfig,
        bundleConfig,
        submit,
        isMobile,
        SwatchesBundleConfigTouchedRef,
    } = context as FBTBundleContextType;
    const { trackFormSections } = useBundleMixpanelHook();

    return (
        <div className={styles.formWrapper}>
            <div style={{ marginBottom: "4px" }}>
                <Divider borderColor="border" />
            </div>
            <div className={styles.collapsibleFormBox}>
                <BundleInfo
                    description={description}
                    title={title}
                    bundle_name={bundle_name}
                    bundleType={BundleTypes.FBT}
                    dispatchBundleConfig={dispatchBundleConfig}
                    offer_name={offer_name}
                />

                <div className={styles.dividerStyle}>
                    <Divider borderColor="border" />
                </div>

                <FormLayout>
                    <div className={styles.customLabelWrapper}>
                        <div className={styles.customLabelWrapper} style={{ marginBottom: "5px" }}>
                            <Label id="fbt_product_selection">Setup Mode</Label>
                        </div>
                    </div>
                    <div className={`${styles.formCol}`}>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                marginTop: "-5px",
                            }}
                        >
                            <RadioButton
                                label={"AI Suggestions"}
                                id="all"
                                helpText={
                                    "AI suggests the best selling product combinations on all your selected product pages"
                                }
                                value={"all"}
                                name="fbt_product_selection"
                                {...asChoiceField(fbt_product_selection, "all")}
                                onChange={(status, val) => {
                                    dispatchBundleConfig({
                                        payload: val,
                                        key: "fbt_product_selection",
                                    });
                                    fbt_product_selection.onChange(val);

                                    dispatchBundleConfig({ payload: [], key: "products" });
                                    products.onChange([]);

                                    dispatchBundleConfig({ payload: [], key: "offered_products" });
                                    offered_products.onChange([]);
                                }}
                            />
                            <RadioButton
                                label={"Manual"}
                                id="manual"
                                helpText="Manually define best selling product combinations for a specific product"
                                value="manual"
                                name="fbt_product_selection"
                                {...asChoiceField(fbt_product_selection, "manual")}
                                onChange={(status, val) => {
                                    dispatchBundleConfig({
                                        payload: val,
                                        key: "fbt_product_selection",
                                    });
                                    fbt_product_selection.onChange(val);
                                }}
                            />
                        </div>
                    </div>
                </FormLayout>

                <div className={styles.dividerStyle}>
                    <Divider borderColor="border" />
                </div>

                <div
                    className={styles.sectionTitleWrapper}
                    onClick={() =>
                        setExpandProductSetup((ps) => {
                            trackFormSections({
                                cta: "Form Section",
                                ctaType: `Product Setup ${ps ? "Collapsed" : "Expanded"}`,
                                bundleType: "standard",
                            });
                            return !ps;
                        })
                    }
                >
                    <div>Product Setup</div>

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            transform: expandProductSetup ? "rotate(90deg)" : "rotate(270deg)",
                        }}
                    >
                        <MdOutlineArrowBackIosNew size={14} />
                    </div>
                </div>

                <Collapsible
                    open={expandProductSetup}
                    id="basic-collapsible"
                    transition={{
                        duration: "500ms",
                        timingFunction: "ease-in-out",
                    }}
                    expandOnPrint
                >
                    {fbt_product_selection?.value === "all" && (
                        <InclusionExclusionProducts
                            limitCheck={ai_fbt_product_limit}
                            limitCheckKey={"ai_fbt_product_limit"}
                            inclusionExclusionType={show_ai_fbt_for}
                            inclusionExclusionTypeKey={"show_ai_fbt_for"}
                            inclusionExclusionArr={ai_fbt_allowed_products}
                            inclusionExclusionArrKey={"ai_fbt_allowed_products"}
                            dispatchBundleConfig={dispatchBundleConfig}
                        />
                    )}

                    {fbt_product_selection?.value === "manual" && (
                        <FormLayout>
                            <div>
                                <div style={{ fontWeight: 600, fontSize: "14px", marginBottom: "8px" }}>
                                    Choose Products to apply Manual Bundle for :
                                </div>
                                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                    <RadioButton
                                        label="All Products"
                                        id="manual_scope_all"
                                        name="manual_product_scope"
                                        value="all"
                                        checked={manual_product_scope?.value === "all"}
                                        onChange={() => {
                                            manual_product_scope.onChange("all");
                                            dispatchBundleConfig({
                                                payload: "all",
                                                key: "manual_product_scope",
                                            });
                                            // Clear main products only; keep offered_products
                                            dispatchBundleConfig({ payload: [], key: "products" });
                                            products.onChange([]);
                                        }}
                                    />
                                    <RadioButton
                                        label="Specific Products"
                                        id="manual_scope_specific"
                                        name="manual_product_scope"
                                        value="specific"
                                        checked={manual_product_scope?.value === "specific"}
                                        onChange={() => {
                                            manual_product_scope.onChange("specific");
                                            dispatchBundleConfig({
                                                payload: "specific",
                                                key: "manual_product_scope",
                                            });
                                        }}
                                    />
                                </div>
                            </div>

                            {/* ── Manual + All Products: show only Suggested products picker ── */}
                            {manual_product_scope?.value === "all" && (
                                <div>
                                    <div
                                        style={{
                                            fontSize: "13px",
                                            color: "#666",
                                            marginBottom: "10px",
                                            padding: "8px 10px",
                                            background: "#f5f5f5",
                                            borderRadius: "6px",
                                            lineHeight: "1.5",
                                        }}
                                    >
                                        This bundle will appear on <strong>every product page</strong>. The page's
                                        product will automatically become the main product. Add the suggested
                                        products that will be shown alongside it.
                                    </div>

                                    <div>
                                        <RenderSelectedProduct
                                            customTitle={"Suggested"}
                                            updateKey={"offered_products"}
                                            bundleLevel={bundle_product_level.value}
                                            dispatchBundleConfig={dispatchBundleConfig}
                                            bundleType={BundleTypes.FBT}
                                            products={offered_products}
                                            submit={submit}
                                            isMobile={isMobile}
                                            excludeProducts={[]}
                                            hideQuantity={true}
                                            multiple={5}
                                        />
                                        <InlineError
                                            message={offered_products.error}
                                            fieldID="offered_products"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* ── Manual + Specific Products: show Main + Suggested pickers ── */}
                            {manual_product_scope?.value === "specific" && (
                                <>
                                    {showBundlerLevel && (
                                        <div className={styles.customLabelWrapper}>
                                            <div
                                                className={styles.customLabelWrapper}
                                                style={{ marginBottom: "5px" }}
                                            >
                                                <Label id="bundle_product_level">Bundle Level</Label>
                                            </div>
                                        </div>
                                    )}
                                    {showBundlerLevel && (
                                        <div className={`${styles.formCol}`}>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    marginTop: "-5px",
                                                }}
                                            >
                                                <RadioButton
                                                    label={"Product Level: Variants in Dropdown"}
                                                    id="product"
                                                    helpText="Selected variants will appear in a dropdown under each product in the bundle."
                                                    value="product"
                                                    name="bundle_product_level"
                                                    {...asChoiceField(bundle_product_level, "product")}
                                                    onChange={(status, val) => {
                                                        dispatchBundleConfig({
                                                            payload: val,
                                                            key: "bundle_product_level",
                                                        });
                                                        dispatchBundleConfig({
                                                            payload: [],
                                                            key: "products",
                                                        });
                                                        products.onChange([]);
                                                        bundle_product_level.onChange(val);
                                                    }}
                                                />
                                                {bundle_product_level.value == "variant" && (
                                                    <RadioButton
                                                        label={"Variant Level: Variants as Standalone"}
                                                        id="variant"
                                                        helpText="Selected variants will be displayed as individual, standalone products in the bundle."
                                                        value="variant"
                                                        name="bundle_product_level"
                                                        {...asChoiceField(bundle_product_level, "variant")}
                                                        onChange={(status, val) => {
                                                            dispatchBundleConfig({
                                                                payload: val,
                                                                key: "bundle_product_level",
                                                            });
                                                            dispatchBundleConfig({
                                                                payload: [],
                                                                key: "products",
                                                            });
                                                            products.onChange([]);
                                                            bundle_product_level.onChange(val);
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    <div>
                                        <RenderSelectedProduct
                                            customTitle={"Main"}
                                            updateKey={"products"}
                                            bundleLevel={bundle_product_level.value}
                                            dispatchBundleConfig={dispatchBundleConfig}
                                            bundleType={BundleTypes.FBT}
                                            products={products}
                                            submit={submit}
                                            isMobile={isMobile}
                                            multiple={5}
                                            hideQuantity={true}
                                        />
                                        <InlineError message={products.error} fieldID="products" />
                                    </div>

                                    <Divider />

                                    <div>
                                        <RenderSelectedProduct
                                            customTitle={"Suggested"}
                                            updateKey={"offered_products"}
                                            bundleLevel={bundle_product_level.value}
                                            dispatchBundleConfig={dispatchBundleConfig}
                                            bundleType={BundleTypes.FBT}
                                            products={offered_products}
                                            submit={submit}
                                            isMobile={isMobile}
                                            excludeProducts={products.value || []}
                                            hideQuantity={true}
                                            multiple={5}
                                        />
                                        <InlineError
                                            message={offered_products.error}
                                            fieldID="offered_products"
                                        />
                                        {false && (
                                            <AllowedProductPages
                                                products={[...products.value]}
                                                dependencyArr={[products]}
                                                validProductPages={allowed_product_pages}
                                                dispatchBundleConfig={dispatchBundleConfig}
                                                updateKey={"allowed_product_pages"}
                                                render_product_pages_type={render_product_pages_type}
                                                bundleType={BundleTypes.FBT}
                                            />
                                        )}
                                    </div>
                                </>
                            )}
                        </FormLayout>
                    )}
                </Collapsible>

                <div className={styles.dividerStyle}>
                    <Divider borderColor="border" />
                </div>

                <Discount
                    dispatchBundleConfig={dispatchBundleConfig}
                    discount_type={discount_type}
                    discount_value={discount_value}
                    order_discounts={order_discounts}
                    product_discounts={product_discounts}
                    shipping_discounts={shipping_discounts}
                    isMobile={isMobile}
                    bundleType={"standard"}
                    products={products}
                    discount_label_template={discount_label_template}
                    discount_label_template_helptext={""}
                />

                <div className={styles.dividerStyle}>
                    <Divider borderColor="border" />
                </div>

                <SwatchesSelectorWrapper
                    dispatchBundleConfig={dispatchBundleConfig}
                    show_swatches={show_swatches}
                    swatchOptions={swatchOptions}
                    swatchesProductsMap={swatchesProductsMap}
                    swatchShape={swatchShape}
                    swatchSize={swatchSize}
                    productSource={{
                        type: (fbt_product_selection?.value=="all" || manual_product_scope?.value=="all") ? "all" : ("specific" as any),
                        value: [...(products?.value || []), ...(offered_products?.value || [])],
                    }}
                    bundleType={BundleTypes.FBT}
                    bundleConfig={bundleConfig}
                    SwatchesBundleConfigTouchedRef={SwatchesBundleConfigTouchedRef}
                />

                <div className={styles.dividerStyle}>
                    <Divider borderColor="border" />
                </div>

                <TextFields
                    dispatchBundleConfig={dispatchBundleConfig}
                    add_to_cart_cta_text={add_to_cart_cta_text}
                    default_cta={default_cta}
                    bundleType={BundleTypes.FBT}
                    footer_text={footer_text}
                    footer_total_text={footer_total_text}
                    free_text={free_text}
                />
            </div>

            <div className={styles.dividerStyle}>
                <Divider borderColor="border" />
            </div>

            <div className={styles.bottomRowCta}>
                <span></span>
                <Button variant="secondary" onClick={() => handleNext(1, "Design")}>
                    Next
                </Button>
            </div>
        </div>
    );
};

export default FBTContentForm;